import React, { useEffect, useState, useCallback } from 'react';
import { merchantApi } from '../../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGauge, faUsers, faClipboardList, faHourglassHalf, faMoneyBill,
  faSackDollar, faHandHoldingDollar, faUniversity, faMoneyBillTransfer,
  faBuilding, faArrowTrendUp, faCoins, faCalendarDays, faChartBar,
  faPercentage,
} from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { fmtBaht, fmtNum } from '../../utils/formatMoney';
import UiPageHeader from '../../components/ui/UiPageHeader';
import UiStatsGrid, { StatCardDef } from '../../components/ui/UiStatsGrid';
import P2pReportPanel from '../../components/P2pReportPanel';

dayjs.extend(utc);
dayjs.extend(timezone);

const BKK = 'Asia/Bangkok';

interface SystemStats {
  total_merchants: number;
  active_merchants: number;
  total_banks: number;
  total_orders: number;
  total_transactions: number;
  matched_transactions: number;
  match_rate: number;
  month_match_rate: number;
  pending_orders: number;
  paid_orders: number;
  today_orders: number;
  today_base_revenue: number;
  today_revenue: number;
  today_fee: number;
  today_payin_fee: number;
  today_payout_fee: number;
  today_p2p_fee?: number;
  today_net_revenue: number;
  month_orders: number;
  month_base_revenue: number;
  month_revenue: number;
  month_fee: number;
  month_payin_fee: number;
  month_payout_fee: number;
  month_p2p_fee?: number;
  month_net_revenue: number;
}

const SectionHeader = ({ icon, title, right }: { icon: any; title: string; right?: React.ReactNode }) => (
  <div className="flex items-center justify-between border-b border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-700/50 px-5 py-4">
    <h2 className="flex items-center gap-2 text-sm font-bold text-gray-700 dark:text-slate-200">
      <FontAwesomeIcon icon={icon} style={{ color: 'var(--theme-primary)' }} />
      {title}
    </h2>
    {right}
  </div>
);

const AdminDashboardPage: React.FC = () => {
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [p2p, setP2p] = useState<any | null>(null); // 💧 P2P เดือนนี้ (แยกจากยอดปกติ)

  const loadStats = useCallback(async () => {
    try {
      const monthFrom = dayjs().tz(BKK).startOf('month').toISOString();
      const monthTo = dayjs().tz(BKK).endOf('day').toISOString();
      const [response, p2pRes] = await Promise.all([
        merchantApi.get('/admin/stats'),
        merchantApi.get('/admin/p2p-report', { params: { date_from: monthFrom, date_to: monthTo } }).catch(() => null),
      ]);
      setStats(response.data.data);
      setP2p(p2pRes?.data?.success ? (p2pRes.data.data.summary ?? null) : null);
    } catch {
      if (statsLoading) toast.error('ไม่สามารถโหลดข้อมูลสถิติได้');
    } finally {
      setStatsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadStats();
    const interval = setInterval(loadStats, 30000);
    return () => clearInterval(interval);
  }, [loadStats]);

  const monthLabel = dayjs().tz(BKK).toDate().toLocaleDateString('th-TH', {
    month: 'long', year: 'numeric',
  });

  const systemCards: StatCardDef[] = stats ? [
    {
      icon: faUsers,
      iconStyle: { background: 'var(--theme-primary)' },
      iconClassName: 'text-white',
      label: 'Total Merchants',
      value: <span style={{ color: 'var(--theme-primary)' }}>{stats.total_merchants}</span>,
      subValue: <span className="text-green-600">{stats.active_merchants} Active</span>,
    },
    {
      icon: faClipboardList,
      iconClassName: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
      label: 'Total Orders',
      value: <span className="text-gray-800 dark:text-slate-100">{fmtNum(stats.total_orders)}</span>,
      subValue: <span className="text-gray-500 dark:text-slate-400">{stats.today_orders} วันนี้</span>,
    },
    {
      icon: faHourglassHalf,
      iconClassName: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
      label: 'Pending Orders',
      value: <span className="text-amber-700 dark:text-amber-400">{stats.pending_orders}</span>,
      subValue: <span className="text-gray-400 dark:text-slate-500">กำลังรอชำระ</span>,
    },
    {
      icon: faMoneyBill,
      iconStyle: { background: 'var(--theme-accent)' },
      iconClassName: 'text-white',
      label: 'ยอดรับเดือนนี้',
      value: <span style={{ color: 'var(--theme-accent)' }}>{fmtBaht(stats.month_revenue)}</span>,
      subValue: <span className="text-gray-500 dark:text-slate-400">{fmtBaht(stats.today_revenue)} วันนี้</span>,
    },
    {
      icon: faSackDollar,
      iconClassName: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400',
      label: 'Fee เดือนนี้',
      value: <span className="text-red-600 dark:text-red-400">-{fmtBaht(stats.month_fee)}</span>,
      subValue: <span className="text-gray-400 dark:text-slate-500">P.In {fmtBaht(stats.month_payin_fee || 0)}{(stats.month_p2p_fee || 0) > 0 ? ` · P2P ${fmtBaht(stats.month_p2p_fee || 0)}` : ''}</span>,
    },
    {
      icon: faHandHoldingDollar,
      iconClassName: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
      label: 'Net เดือนนี้',
      value: <span className="text-green-700 dark:text-green-400">{fmtBaht(stats.month_net_revenue)}</span>,
      subValue: <span className="text-gray-500 dark:text-slate-400">{fmtBaht(stats.today_net_revenue)} วันนี้</span>,
    },
    {
      icon: faUniversity,
      iconClassName: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400',
      label: 'Total Banks',
      value: <span className="text-indigo-700 dark:text-indigo-400">{stats.total_banks}</span>,
      subValue: <span className="text-gray-400 dark:text-slate-500">ทุก Merchants</span>,
    },
    {
      icon: faMoneyBillTransfer,
      iconClassName: 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300',
      label: 'Transactions',
      value: <span className="text-gray-700 dark:text-slate-200">{fmtNum(stats.total_transactions)}</span>,
      subValue: <span className="text-gray-400 dark:text-slate-500">จากธนาคาร</span>,
    },
  ] : [];

  const monthlyCards: StatCardDef[] = stats ? [
    {
      icon: faClipboardList,
      iconClassName: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400',
      label: 'Orders เดือนนี้',
      value: <span className="text-indigo-700 dark:text-indigo-400">{fmtNum(stats.month_orders)}</span>,
    },
    {
      icon: faPercentage,
      iconClassName: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
      label: 'Success Rate',
      value: <span className="text-green-700 dark:text-green-400">{(stats.month_match_rate ?? 0).toFixed(1)}%</span>,
    },
    {
      icon: faCoins,
      iconStyle: { background: 'var(--theme-primary)' },
      iconClassName: 'text-white',
      label: 'ยอดฝากจริง (Base)',
      value: <span style={{ color: 'var(--theme-primary)' }}>{fmtBaht(stats.month_base_revenue)}</span>,
    },
    {
      icon: faMoneyBill,
      iconStyle: { background: 'var(--theme-accent)' },
      iconClassName: 'text-white',
      label: 'ยอดรับจริง (รวมสตางค์)',
      value: <span style={{ color: 'var(--theme-accent)' }}>{fmtBaht(stats.month_revenue)}</span>,
    },
    {
      icon: faSackDollar,
      iconClassName: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400',
      label: 'ค่าธรรมเนียม (Fee)',
      value: <span className="text-red-600 dark:text-red-400">-{fmtBaht(stats.month_fee)}</span>,
      subValue: (
        <span className="text-gray-400 dark:text-slate-500">
          P.In {fmtBaht(stats.month_payin_fee || 0)} · P.Out {fmtBaht(stats.month_payout_fee || 0)}{(stats.month_p2p_fee || 0) > 0 ? ` · P2P ${fmtBaht(stats.month_p2p_fee || 0)}` : ''}
        </span>
      ),
    },
    {
      icon: faHandHoldingDollar,
      iconClassName: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
      label: 'ยอดสุทธิ (Net)',
      value: <span className="text-green-700 dark:text-green-400">{fmtBaht(stats.month_net_revenue)}</span>,
    },
  ] : [];

  const todayCards: StatCardDef[] = stats ? [
    {
      icon: faClipboardList,
      iconClassName: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400',
      label: 'Orders วันนี้',
      value: <span className="text-gray-800 dark:text-slate-100">{stats.today_orders}</span>,
    },
    {
      icon: faCoins,
      iconStyle: { background: 'var(--theme-primary)' },
      iconClassName: 'text-white',
      label: 'ยอดฝาก (Base)',
      value: <span style={{ color: 'var(--theme-primary)' }}>{fmtBaht(stats.today_base_revenue)}</span>,
    },
    {
      icon: faMoneyBill,
      iconStyle: { background: 'var(--theme-accent)' },
      iconClassName: 'text-white',
      label: 'ยอดรับ (รวมสตางค์)',
      value: <span style={{ color: 'var(--theme-accent)' }}>{fmtBaht(stats.today_revenue)}</span>,
    },
    {
      icon: faSackDollar,
      iconClassName: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400',
      label: 'ค่าธรรมเนียม',
      value: <span className="text-red-600 dark:text-red-400">-{fmtBaht(stats.today_fee)}</span>,
      subValue: (
        <span className="text-gray-400 dark:text-slate-500">
          P.In {fmtBaht(stats.today_payin_fee || 0)} · P.Out {fmtBaht(stats.today_payout_fee || 0)}{(stats.today_p2p_fee || 0) > 0 ? ` · P2P ${fmtBaht(stats.today_p2p_fee || 0)}` : ''}
        </span>
      ),
    },
    {
      icon: faHandHoldingDollar,
      iconClassName: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
      label: 'ยอดสุทธิ (Net)',
      value: <span className="text-green-700 dark:text-green-400">{fmtBaht(stats.today_net_revenue)}</span>,
    },
  ] : [];

  const merchantCards: StatCardDef[] = stats ? [
    {
      icon: faBuilding,
      iconStyle: { background: 'var(--theme-primary)' },
      iconClassName: 'text-white',
      label: 'Total Merchants',
      value: <span style={{ color: 'var(--theme-primary)' }}>{stats.total_merchants}</span>,
    },
    {
      icon: faUsers,
      iconClassName: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
      label: 'Active',
      value: <span className="text-green-700 dark:text-green-400">{stats.active_merchants}</span>,
    },
    {
      icon: faUsers,
      iconClassName: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400',
      label: 'Inactive',
      value: <span className="text-red-600 dark:text-red-400">{stats.total_merchants - stats.active_merchants}</span>,
    },
    {
      icon: faClipboardList,
      iconClassName: 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300',
      label: 'Avg Orders/Merchant',
      value: (
        <span className="text-gray-700 dark:text-slate-200">
          {stats.total_merchants > 0 ? (stats.total_orders / stats.total_merchants).toFixed(1) : '0'}
        </span>
      ),
    },
  ] : [];

  return (
    <div className="w-full">
      <UiPageHeader
        icon={faGauge}
        title="Admin Control Center"
        subtitle={`ระบบ Admin • ${stats?.total_merchants ?? 0} Merchants`}
      />

      <UiStatsGrid cards={systemCards} loading={statsLoading} cols={4} />

      <div className="mb-6 grid grid-cols-1 gap-4 xl:grid-cols-3">
        {/* สรุปรายได้เดือนนี้ */}
        <div className="overflow-hidden rounded-xl border border-gray-100 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm xl:col-span-2">
          <SectionHeader
            icon={faArrowTrendUp}
            title="สรุปรายได้เดือนนี้"
            right={
              <span className="inline-flex items-center gap-1.5 rounded-full border border-indigo-200 dark:border-indigo-700 bg-indigo-100 dark:bg-indigo-900/30 px-3 py-1 text-xs font-semibold text-indigo-600 dark:text-indigo-400">
                <FontAwesomeIcon icon={faCalendarDays} className="text-[10px]" />
                {monthLabel}
              </span>
            }
          />
          <div className="p-4 sm:p-5">
            <UiStatsGrid cards={monthlyCards} loading={statsLoading} cols={3} className="!mb-0" />
          </div>
        </div>

        {/* สถิติวันนี้ */}
        <div className="overflow-hidden rounded-xl border border-gray-100 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm">
          <SectionHeader icon={faChartBar} title="สถิติวันนี้" />
          <div className="p-4 sm:p-5">
            <UiStatsGrid cards={todayCards} loading={statsLoading} cols={2} className="!mb-0" />
          </div>
        </div>
      </div>

      {/* 💧 P2P เดือนนี้ — component กลาง (มีบรรทัด fallback เข้าบัญชีเว็บด้วย) */}
      <P2pReportPanel summary={p2p} title={`P2P — ${monthLabel} (แยกจากยอดปกติ)`} />

      {/* Merchants Overview */}
      <div className="overflow-hidden rounded-xl border border-gray-100 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm">
        <SectionHeader icon={faBuilding} title="Merchants Overview" />
        <div className="p-4 sm:p-5">
          <UiStatsGrid cards={merchantCards} loading={statsLoading} cols={4} className="!mb-0" />
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
